import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Save, X } from 'lucide-react'
import FlowEditor, { type FlowEditorHandle } from './FlowEditor'
import { FlowVisualEditor } from './flow-visual/FlowVisualEditor'
import type { FlowConfigData } from '../types/flowConfig.types'
import type { FlowConfigUpdater } from '../types/flowConfigUpdater'
import { flowConfigSemanticEqual, pruneUiLayoutToSteps } from '../utils/flowUiLayout'

/** Separadores do editor: diagrama (@xyflow) vs formulários clássicos. */
const FLOW_EDITOR_VIEW_TAB = {
  VISUAL: 'visual',
  FORMS: 'forms'
} as const

type FlowEditorViewTab = (typeof FLOW_EDITOR_VIEW_TAB)[keyof typeof FLOW_EDITOR_VIEW_TAB]

export interface FlowEditorWorkspaceProps {
  initialFlowConfig: FlowConfigData
  /** Reinicia diagrama e `ui_layout` em memória quando a sessão muda (outro fluxo / criar novo). */
  sessionKey: string
  onSave: (config: FlowConfigData) => void | Promise<void>
  onCancel: () => void
  isEditing: boolean
}

/**
 * Envolve o FlowEditor clássico com separador para o diagrama visual.
 * O FlowEditor permanece montado (oculto no separador Visual) para não perder rascunho e permitir Salvar/Cancelar.
 */
export function FlowEditorWorkspace({
  initialFlowConfig,
  sessionKey,
  onSave,
  onCancel,
  isEditing
}: FlowEditorWorkspaceProps) {
  const editorRef = useRef<FlowEditorHandle>(null)
  /** Fonte imediata para `handleVisualFlowChange` (evita base errada entre ticks). */
  const diagramConfigRef = useRef<FlowConfigData>(initialFlowConfig)
  const [activeTab, setActiveTab] = useState<FlowEditorViewTab>(FLOW_EDITOR_VIEW_TAB.FORMS)
  const [diagramConfig, setDiagramConfig] = useState<FlowConfigData>(initialFlowConfig)
  const [stagedUiLayout, setStagedUiLayout] = useState<Record<string, { x: number; y: number }>>(
    () => initialFlowConfig.ui_layout ?? {}
  )
  const [visualSavePending, setVisualSavePending] = useState(false)

  useEffect(() => {
    setDiagramConfig(initialFlowConfig)
    diagramConfigRef.current = initialFlowConfig
    setStagedUiLayout(initialFlowConfig.ui_layout ?? {})
    // Apenas ao mudar de fluxo / modo criar; não incluir initialFlowConfig (nova referência a cada render do pai).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sessionKey é a fonte de verdade da sessão
  }, [sessionKey])

  useLayoutEffect(() => {
    diagramConfigRef.current = diagramConfig
  }, [diagramConfig])

  /** Remover posições de etapas que já não existem (ex.: delete nos Formulários sem passar pelo fluxo visual). */
  const diagramStepIdsKey = Object.keys(diagramConfig.steps || {})
    .sort()
    .join('|')
  useEffect(() => {
    const ids = new Set(Object.keys(diagramConfig.steps || {}))
    setStagedUiLayout((prev) => {
      let changed = false
      const next: Record<string, { x: number; y: number }> = {}
      for (const [k, v] of Object.entries(prev)) {
        if (ids.has(k)) {
          next[k] = v
        } else {
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [diagramStepIdsKey])

  const visualFlowConfig = useMemo(() => {
    const merged: FlowConfigData = {
      ...diagramConfig,
      ui_layout: {
        ...(diagramConfig.ui_layout ?? {}),
        ...stagedUiLayout
      }
    }
    return pruneUiLayoutToSteps(merged)
  }, [diagramConfig, stagedUiLayout])

  const handleSaveMerged = useCallback(
    async (data: FlowConfigData) => {
      await onSave({
        ...data,
        ui_layout: {
          ...(data.ui_layout ?? {}),
          ...stagedUiLayout
        }
      })
    },
    [onSave, stagedUiLayout]
  )

  const handleSaveFromVisualTab = useCallback(async () => {
    setVisualSavePending(true)
    try {
      await editorRef.current?.save()
    } finally {
      setVisualSavePending(false)
    }
  }, [])

  const handleDiagramSyncFromEditor = useCallback(
    (c: FlowConfigData) => {
      setDiagramConfig((prev) => {
        const pruned = pruneUiLayoutToSteps(c)
        if (flowConfigSemanticEqual(prev, pruned)) return prev
        /**
         * No modo Visual o diagrama é atualizado por `handleVisualFlowChange` + `replaceConfig`.
         * O FlowEditor fica oculto e o seu `config` pode ainda listar passos já removidos; qualquer
         * `onConfigChange` que não seja estritamente igual ao diagrama atual reintroduziria esses passos.
         */
        if (activeTab === FLOW_EDITOR_VIEW_TAB.VISUAL) {
          return prev
        }
        return pruned
      })
    },
    [activeTab]
  )

  const handleVisualFlowChange = useCallback((update: FlowConfigUpdater) => {
    /** Após cada alteração visual, o FlowEditor deve ficar igual ao `diagramConfig` **no mesmo turno**.
     * Se `replaceConfig` for adiado (ex.: microtask), o próximo `getConfig()` ainda traz passos já removidos
     * e o merge ao adicionar um bloco restaura esses passos. */
    const syncPayload = { current: null as FlowConfigData | null }
    setDiagramConfig((prev) => {
      /** `prev` é o estado canónico do diagrama neste batch (React garante o mais recente). */
      const base = prev
      const rawNext = typeof update === 'function' ? update(base) : update
      const next = pruneUiLayoutToSteps(rawNext)
      if (flowConfigSemanticEqual(prev, next)) {
        return prev
      }
      syncPayload.current = next
      return next
    })
    const payload = syncPayload.current
    if (payload) {
      diagramConfigRef.current = payload
      editorRef.current?.replaceConfig(payload)
      setStagedUiLayout(payload.ui_layout ?? {})
    }
  }, [])

  const tabButtonClass = (tab: FlowEditorViewTab) =>
    `rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
      activeTab === tab
        ? 'bg-white text-gray-900 shadow-sm'
        : 'text-gray-600 hover:text-gray-900'
    }`

  return (
    <div className="h-full flex flex-col min-h-[calc(100vh-8rem)] bg-gray-50">
      <div className="shrink-0 bg-white border-b border-gray-200 px-4 pt-3 pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex gap-1 rounded-lg bg-gray-100 p-1 w-fit" role="tablist" aria-label="Modo de edição do fluxo">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === FLOW_EDITOR_VIEW_TAB.FORMS}
              className={tabButtonClass(FLOW_EDITOR_VIEW_TAB.FORMS)}
              onClick={() => setActiveTab(FLOW_EDITOR_VIEW_TAB.FORMS)}
            >
              Formulários
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === FLOW_EDITOR_VIEW_TAB.VISUAL}
              className={tabButtonClass(FLOW_EDITOR_VIEW_TAB.VISUAL)}
              onClick={() => setActiveTab(FLOW_EDITOR_VIEW_TAB.VISUAL)}
            >
              Visual
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col">
        {activeTab === FLOW_EDITOR_VIEW_TAB.VISUAL ? (
          <div className="flex-1 flex flex-col min-h-0 p-4 overflow-auto">
            <FlowVisualEditor
              key={sessionKey}
              flowConfig={visualFlowConfig}
              onUiLayoutChange={setStagedUiLayout}
              onFlowConfigChange={handleVisualFlowChange}
              editable
              className="flex-1"
            />
            <div className="flex flex-wrap justify-end gap-2 mt-4 pt-4 border-t border-gray-200 shrink-0">
              <button
                type="button"
                onClick={onCancel}
                className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <X className="w-4 h-4 mr-2" />
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void handleSaveFromVisualTab()}
                disabled={visualSavePending}
                className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <Save className="w-4 h-4 mr-2" />
                {visualSavePending ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        ) : null}

        <div
          className={
            activeTab === FLOW_EDITOR_VIEW_TAB.FORMS
              ? 'flex-1 flex flex-col min-h-0 overflow-hidden'
              : 'hidden'
          }
          aria-hidden={activeTab !== FLOW_EDITOR_VIEW_TAB.FORMS}
        >
          <FlowEditor
            key={sessionKey}
            ref={editorRef}
            flowConfig={initialFlowConfig}
            onSave={handleSaveMerged}
            onCancel={onCancel}
            isEditing={isEditing}
            onConfigChange={handleDiagramSyncFromEditor}
          />
        </div>
      </div>
    </div>
  )
}
